/**
 * Pure form logic for LotCostSection (T3-B-03c).
 *
 * Kept in a testable module (no React) so validation, defaults, and payload
 * shaping can be unit-tested without a React test harness.
 */

import type { LotCostEventCreatePayload } from '../../utils/lotCostEventApi'
import {
  DEFAULT_CLASSIFICATION_BY_PREFIX,
  DEFAULT_NATURE_BY_PREFIX,
  type CategoryPrefix,
  type CostClassification,
  type ExpenseNature
} from '../../utils/lotCostEventApi'

/**
 * Only Genetics, Health, and Labor are manually entered from the lot card.
 * Nutrition (N) is auto-derived at the feedLogApi boundary (T3-B-03b).
 * Insurance/Marketing/Administration/Finance/Rent are farm-scoped fixed costs
 * that may get a dedicated Farm card entry in a follow-up card.
 */
export const MANUAL_CATEGORY_PREFIXES = ['G', 'H', 'L'] as const
export type ManualCategoryPrefix = typeof MANUAL_CATEGORY_PREFIXES[number]

export type LotCostFormDraft = {
  categoryPrefix: ManualCategoryPrefix
  amountMajor: string          // operator-friendly input like "12.50"
  currency: string             // 3-letter ISO, uppercased on save
  recordedAt: string           // YYYY-MM-DD (native date input)
  expenseNature: ExpenseNature
  costClassification: CostClassification
  notes: string
}

export type LotCostFormFieldError =
  | 'amountRequired'
  | 'amountInvalid'
  | 'currencyInvalid'
  | 'dateInvalid'

export type LotCostFormErrors = Partial<Record<keyof LotCostFormDraft, LotCostFormFieldError>>

export type LotCostFormValidationResult =
  | {
      ok: true
      payload: Pick<
        LotCostEventCreatePayload,
        'categoryPrefix' | 'amountCents' | 'currency' | 'recordedAt' | 'expenseNature' | 'costClassification' | 'notes'
      >
    }
  | { ok: false; errors: LotCostFormErrors }

const todayIso = (): string => {
  const now = new Date()
  const y = now.getFullYear()
  const m = String(now.getMonth() + 1).padStart(2, '0')
  const d = String(now.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/**
 * Fresh draft with sensible defaults: Genetics prefix, today's date, USD,
 * variable + direct (Genetics defaults).
 *
 * v2.2 T3-B-05b: accepts an optional `preferredCurrency` (typically the
 * most-recent currency on the selected lot's existing events, resolved
 * via `resolvePreferredCurrency`). When present and valid (3-letter ISO
 * shape, case-insensitive) the draft opens with that currency
 * pre-filled instead of the `USD` fallback. Invalid / empty / undefined
 * input keeps the historical `'USD'` default so tenants without prior
 * events see the same behavior as before.
 */
export const buildInitialLotCostFormDraft = (preferredCurrency?: string): LotCostFormDraft => ({
  categoryPrefix: 'G',
  amountMajor: '',
  currency: normalizePreferredCurrency(preferredCurrency),
  recordedAt: todayIso(),
  expenseNature: DEFAULT_NATURE_BY_PREFIX.G,
  costClassification: DEFAULT_CLASSIFICATION_BY_PREFIX.G,
  notes: ''
})

const normalizePreferredCurrency = (raw: string | undefined): string => {
  if (typeof raw !== 'string') return 'USD'
  const trimmed = raw.trim()
  if (!/^[A-Za-z]{3}$/.test(trimmed)) return 'USD'
  return trimmed.toUpperCase()
}

/**
 * v2.2 T3-B-05b: resolves the currency likely intended for a new cost
 * event on a lot, given the lot's existing event history. Returns the
 * currency of the most-recently-recorded event (by `recordedAt` desc,
 * ties broken by string compare on the currency code). Returns
 * `undefined` when the input is empty so `buildInitialLotCostFormDraft`
 * falls back to its `'USD'` default.
 *
 * Pure and side-effect free; does not mutate the input.
 */
export const resolvePreferredCurrency = (
  events: readonly { currency: string; recordedAt: string }[]
): string | undefined => {
  if (events.length === 0) return undefined
  let winner: { currency: string; recordedAt: string } | undefined
  for (const event of events) {
    if (typeof event.currency !== 'string') continue
    const code = event.currency.trim()
    if (!code) continue
    if (!winner) {
      winner = { currency: code, recordedAt: event.recordedAt ?? '' }
      continue
    }
    const cmpDate = (event.recordedAt ?? '').localeCompare(winner.recordedAt)
    if (cmpDate > 0) {
      winner = { currency: code, recordedAt: event.recordedAt ?? '' }
    } else if (cmpDate === 0) {
      // Deterministic tie-break: string compare on currency code.
      if (code.localeCompare(winner.currency) < 0) {
        winner = { currency: code, recordedAt: event.recordedAt ?? '' }
      }
    }
  }
  return winner?.currency
}

/**
 * Reapplies nature + classification defaults for the given category prefix.
 * Called when the operator changes the category in the form so the toggles
 * reflect the accounting convention for that category.
 */
export const applyDefaultsForCategory = (
  draft: LotCostFormDraft,
  prefix: ManualCategoryPrefix
): LotCostFormDraft => ({
  ...draft,
  categoryPrefix: prefix,
  expenseNature: DEFAULT_NATURE_BY_PREFIX[prefix as CategoryPrefix],
  costClassification: DEFAULT_CLASSIFICATION_BY_PREFIX[prefix as CategoryPrefix]
})

/**
 * Parses "12.5" or "12,5" -> 12.5. Returns null if not finite.
 */
const parseAmountMajor = (raw: string): number | null => {
  const trimmed = raw.trim().replaceAll(',', '.')
  if (!trimmed) return null
  const parsed = Number(trimmed)
  return Number.isFinite(parsed) ? parsed : null
}

/**
 * Validates the draft and, on success, returns the LotCostEvent payload
 * fragment (caller supplies farmId + lotId + productionUnitId).
 */
export const validateLotCostFormDraft = (draft: LotCostFormDraft): LotCostFormValidationResult => {
  const errors: LotCostFormErrors = {}

  const trimmedAmount = draft.amountMajor.trim()
  const trimmedCurrency = draft.currency.trim().toUpperCase()
  const trimmedDate = draft.recordedAt.trim()

  if (!trimmedAmount) {
    errors.amountMajor = 'amountRequired'
  } else {
    const amount = parseAmountMajor(trimmedAmount)
    if (amount === null || amount < 0) {
      errors.amountMajor = 'amountInvalid'
    }
  }

  if (!/^[A-Z]{3}$/.test(trimmedCurrency)) {
    errors.currency = 'currencyInvalid'
  }

  if (!trimmedDate || Number.isNaN(new Date(`${trimmedDate}T00:00:00Z`).getTime())) {
    errors.recordedAt = 'dateInvalid'
  }

  if (Object.keys(errors).length > 0) {
    return { ok: false, errors }
  }

  const amount = parseAmountMajor(trimmedAmount) as number
  const amountCents = Math.round(amount * 100)
  const trimmedNotes = draft.notes.trim()

  return {
    ok: true,
    payload: {
      categoryPrefix: draft.categoryPrefix as CategoryPrefix,
      amountCents,
      currency: trimmedCurrency,
      recordedAt: `${trimmedDate}T00:00:00Z`,
      expenseNature: draft.expenseNature,
      costClassification: draft.costClassification,
      ...(trimmedNotes ? { notes: trimmedNotes } : {})
    }
  }
}

/**
 * Formats an integer cents amount for display: "$12.50 USD" style.
 * Not locale-heavy — matches the rest of Sunday's inline display style.
 */
export const formatCostEventDisplayAmount = (amountCents: number, currency: string): string => {
  const major = (amountCents / 100).toFixed(2)
  return `${major} ${currency}`
}

/**
 * Sums an array of cost events by currency. Returns an alphabetically-sorted
 * array of [currency, totalCents] pairs. A lot could in principle carry
 * mixed-currency events, so we don't attempt FX conversion — the UI displays
 * each currency total independently. Used by T3-B-03d totals surface.
 */
export const sumCostEventsByCurrency = (
  events: readonly { currency: string; amountCents: number }[]
): Array<[currency: string, totalCents: number]> => {
  const totals = new Map<string, number>()
  for (const event of events) {
    totals.set(event.currency, (totals.get(event.currency) ?? 0) + event.amountCents)
  }
  return Array.from(totals.entries()).sort(([a], [b]) => a.localeCompare(b))
}

/**
 * Cost per 1,000 live fry (findings #102, success criterion F2): the lot's own
 * costs ÷ (live fry / 1,000), one figure per currency. Facility-wide costs are
 * not allocated, so they are not in it. Empty when nothing is alive.
 */
export const costPerThousandFry = (
  totals: ReadonlyArray<readonly [currency: string, totalCents: number]>,
  alive: number | null
): Array<[currency: string, centsPerThousand: number]> => {
  if (alive === null || !Number.isFinite(alive) || alive <= 0) return []
  return totals.map(([currency, cents]) => [currency, Math.round(cents / (alive / 1000))])
}
